import { useCallback, useEffect } from 'react';
import { getFlexiblePrecision, getFlexibleRange, getTaskEnd, getTaskStartDate } from './scheduleUtils';
import { PlannerTaskInfo } from './types';
import { PlannerRefs } from './usePlannerRefs';
import { PlannerState } from './usePlannerState';

export const useTimelineTaskQueries = (
    tasks: PlannerTaskInfo[], state: PlannerState, refs: PlannerRefs
) => {
    const { setTimeSlotHeight, showSchedule, timelineScale } = state;
    const getTasksForDay = useCallback((date: Date) => {
        return tasks.filter(({ task }) => {
            const precision = getFlexiblePrecision(task);
            if (precision === 'month' || precision === 'year') return false;
            if (precision !== 'day') {
                const taskStart = getTaskStartDate(task);
                if (!taskStart) return false;
                const taskEnd = getTaskEnd(task);
                const dayStart = new Date(date);
                dayStart.setHours(0, 0, 0, 0);
                const dayEnd = new Date(dayStart);
                dayEnd.setDate(dayEnd.getDate() + 1);
                return taskStart < dayEnd && taskEnd > dayStart;
            }
            const { start, end } = getFlexibleRange(task);
            return date >= start && date <= end;
        });
    }, [tasks]);
    const getTasksForMonth = useCallback((date: Date) => {
        const year = date.getFullYear();
        const month = date.getMonth();
        return tasks.filter(({ task }) => {
            const precision = getFlexiblePrecision(task);
            if (precision === 'year') return false;
            if (precision !== 'month') {
                const taskDate = getTaskStartDate(task);
                if (!taskDate) return false;
                return taskDate.getFullYear() === year && taskDate.getMonth() === month;
            }
            const { start, end } = getFlexibleRange(task);
            return start <= new Date(year, month + 1, 0, 23, 59, 59, 999) && end >= new Date(year, month, 1);
        });
    }, [tasks]);
    const getTasksForYear = useCallback((year: number) => tasks.filter(({ task }) => {
        if (getFlexiblePrecision(task) !== 'year') return getTaskStartDate(task)?.getFullYear() === year;
        const { start, end } = getFlexibleRange(task);
        return start.getFullYear() <= year && end.getFullYear() >= year;
    }), [tasks]);
    useEffect(() => {
        if (refs.timelineRef.current && !showSchedule && timelineScale === 'day') {
            const first = refs.timelineRef.current.querySelector('.timeline-slot:not(.timeline-slot-gap)') as HTMLElement | null;
            if (first) setTimeSlotHeight(first.offsetHeight);
        }
    }, [refs.timelineRef, setTimeSlotHeight, showSchedule, timelineScale]);
    return { getTasksForDay, getTasksForMonth, getTasksForYear };
};
